import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Map, { MapRef, Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  TripLodging,
  HotelInitial,
  AccommodationListing,
  ViewLodging,
} from "@/types";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import { Tooltip } from "@heroui/tooltip";
import {
  Bath,
  Bed,
  Home,
  Loader2,
  Search,
  Users,
  Calendar,
} from "lucide-react";
import {
  formatImageUrl,
  formatPrice,
  formatReviewCount,
  formatReviewScore,
} from "@/lib/general";
import LazyLoad from "../../image/LazyLoad";
import { AnimatePresence, motion } from "framer-motion";
import LodgingsSidebar from "./LodgingsSidebar";
import { convertHotelIntoTripLodging } from "@/lib/accommodation/agodaHotels";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { DateTime } from "luxon";
import { convertHomeStayIntoTripLodging } from "@/lib/accommodation/homes";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { BookingModal } from "@/components/common/BookingModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useBookingStatus } from "@/hooks/useBookingStatus";
import { format } from "date-fns";
import logger from "@/utils/logger";

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;

const loadingMessages = [
  "Searching...",
  "Finding nearby accommodations...",
  "Checking availability...",
  "Getting the best rates...",
  "Almost there...",
];

interface LodgingsEmptyComponentProps {
  locationName: string;
  lodgings: TripLodging[];
  checkInDate?: string;
  checkOutDate?: string;
  isAdmin: boolean;
}

interface FloatingHeaderProps {
  isOpen: boolean;
  mainLodging: TripLodging | null;
  tripId: string;
  effectiveCheckIn: string;
  effectiveCheckOut: string;
}

const FloatingHeader: React.FC<FloatingHeaderProps> = ({
  isOpen,
  mainLodging,
  tripId,
  effectiveCheckIn,
  effectiveCheckOut,
}) => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [lastBookingAttemptTime, setLastBookingAttemptTime] = useState<
    number | null
  >(null);
  const { updateBookingStatus } = useBookingStatus();
  const { selectedLocation } = useTripStore();

  // Check if user returned from booking page
  useEffect(() => {
    const checkIfReturnedFromBooking = () => {
      if (lastBookingAttemptTime) {
        const timeElapsed = Date.now() - lastBookingAttemptTime;
        // If less than 15 minutes have passed since booking attempt
        if (timeElapsed < 15 * 60 * 1000) {
          setShowBookingConfirmation(true);
          setLastBookingAttemptTime(null);
        }
      }
    };

    // Check when window regains focus
    const handleFocus = () => {
      checkIfReturnedFromBooking();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastBookingAttemptTime]);

  const handleBookNow = () => {
    if (!mainLodging || !tripId || !effectiveCheckIn || !effectiveCheckOut) {
      logger.log(
        "No mainLodging or tripId or effectiveCheckIn or effectiveCheckOut"
      );
      return;
    }

    setShowBookingModal(true);
  };

  const handleProviderBooked = () => {
    setLastBookingAttemptTime(Date.now());
    setShowBookingModal(false);
  };

  const handleConfirmBooking = () => {
    if (!mainLodging || !selectedLocation) return;
    updateBookingStatus(
      "accommodation",
      mainLodging.id.toString(),
      "booked",
      selectedLocation.id,
      selectedLocation.date.start_date
    );
    setShowBookingConfirmation(false);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        }}
        transition={{ duration: 0.2 }}
        className="absolute flex flex-col items-start justify-center top-4 right-[12.8rem] z-40 bg-white rounded-lg shadow-md px-4 py-2"
      >
        <div className="flex flex-row space-x-2 items-center w-full">
          <i className="text-sm fa-solid fa-house"></i>
          <h2 className="text-sm font-medium text-neutral-800">Added Stays</h2>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        }}
        transition={{ duration: 0.2 }}
      >
        <button
          onClick={handleBookNow}
          className="absolute flex flex-row space-x-2 items-center justify-center top-4 right-[5rem] z-40 text-white bg-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-300 shadow-lg hover:shadow-indigo-500/20 disabled:opacity-50 disabled:hover:bg-indigo-600 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
        >
          <span className="text-sm font-medium tracking-wide">Book now</span>
          <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5"></i>
        </button>
      </motion.div>

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        type="accommodation"
        tripId={tripId}
        lodgingData={{
          id: mainLodging?.id.toString() || "",
          name: mainLodging?.name || "",
          checkIn: effectiveCheckIn,
          checkOut: effectiveCheckOut,
          type: mainLodging?.type || "hotel",
        }}
        onBooked={handleProviderBooked}
      />

      <Dialog
        open={showBookingConfirmation}
        onOpenChange={setShowBookingConfirmation}
      >
        <DialogContent className="p-6 bg-white rounded-xl">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl font-semibold">
              Did you book your accommodation?
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-600 font-light">
              Update your booking status for your accommodation
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 mt-2 rounded-xl bg-neutral-50 border border-neutral-200">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-200 flex-shrink-0">
                {mainLodging?.mainImageURL ? (
                  <img
                    src={formatImageUrl(mainLodging.mainImageURL, "medium")}
                    alt={mainLodging.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="fas fa-image text-neutral-400 text-xl" />
                  </div>
                )}
              </div>
              <div className="w-full min-w-0">
                <h3 className="text-sm font-medium text-neutral-900 line-clamp-1">
                  {mainLodging?.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-xs text-neutral-500 font-light">
                    {effectiveCheckIn && effectiveCheckOut ? (
                      <>
                        {format(new Date(effectiveCheckIn), "MMM d")} -{" "}
                        {format(new Date(effectiveCheckOut), "MMM d")}
                      </>
                    ) : (
                      "Dates not set"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowBookingConfirmation(false)}
              className="flex-1 rounded-full font-light text-sm hover:bg-neutral-100"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Not Yet
            </Button>
            <Button
              onClick={handleConfirmBooking}
              className="flex-1 bg-green-500/10 border border-green-500/50 text-green-700 hover:bg-green-500/20 rounded-full font-light text-sm"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Yes, I booked it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const LodgingsEmptyComponent: React.FC<LodgingsEmptyComponentProps> = ({
  locationName,
  lodgings,
  checkInDate,
  checkOutDate,
  isAdmin,
}) => {
  const {
    accommodationMapViewport,
    setAccommodationBounds,
    setAccommodationMapViewport,
  } = useTransportStore();
  const {
    activeAccommodationTab,
    responseHotels,
    responseHomeStays,
    setShouldSearchArea,
    isFetchingHotels,
    isFetchingHomeStays,
    viewLodging,
    setViewLodging,
    lastAddedHotel,
    showAddNotification,
    setLastAddedHotel,
    setShowAddNotification,
    addLodging,
    setMainLodging,
    handleModalOpenHomeStay,
    handleModalOpenMainLodging,
  } = useAccommodationStore();
  const { getEffectiveArrivalDate } = useTransportStore();
  const { tripData, selectedLocation } = useTripStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef>(null);
  const [isFlying, setIsFlying] = useState(false);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState<number>(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isLoading = useMemo(() => {
    if (activeAccommodationTab === "hotels") {
      return isFetchingHotels;
    }
    if (activeAccommodationTab === "home-stays") {
      return isFetchingHomeStays;
    }
    return false;
  }, [isFetchingHotels, isFetchingHomeStays, activeAccommodationTab]);

  useEffect(() => {
    return () => {
      setHoverId(null);
      setIsFlying(false);
    };
  }, [setViewLodging]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        if (map) {
          map.remove();
        }
      }
    };
  }, []);

  const updateBounds = useCallback(() => {
    if (mapRef.current) {
      const mapInstance = mapRef.current.getMap();
      const bounds = mapInstance.getBounds();
      if (!bounds) return;
      setAccommodationBounds({
        _sw: {
          lng: bounds.getWest(),
          lat: bounds.getSouth(),
        },
        _ne: {
          lng: bounds.getEast(),
          lat: bounds.getNorth(),
        },
      });
    }
  }, [setAccommodationBounds]);

  // fly to the viewLodging location when the marker is clicked
  const flyToViewLodging = useCallback(() => {
    setIsFlying(true);
    if (mapRef.current && viewLodging) {
      mapRef.current.flyTo({
        center: [viewLodging.longitude, viewLodging.latitude],
        duration: 1000,
      });
      setTimeout(() => setIsFlying(false), 1200); // Reduced timeout to match duration
    }
  }, [viewLodging]);

  useEffect(() => {
    flyToViewLodging();
  }, [viewLodging, flyToViewLodging]);

  const onMove = useCallback(
    (evt: any) => {
      const newBounds = evt.target.getBounds();
      setAccommodationMapViewport({
        ...evt.viewState,
      });
    },
    [setAccommodationMapViewport]
  );

  const handleSearchThisArea = () => {
    if (mapRef.current) {
      logger.log("search this area");
      updateBounds();
      setShouldSearchArea(true);
    }
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (showAddNotification) {
      setShowAddNotification(false);
    }
  };

  const effectiveCheckIn = useMemo(() => {
    const date = getEffectiveArrivalDate(
      tripData!.transport,
      selectedLocation!.id
    );
    return date ? date : selectedLocation!.date.start_date;
  }, [tripData, selectedLocation, getEffectiveArrivalDate]);

  const effectiveCheckOut = useMemo(() => {
    return DateTime.fromISO(selectedLocation!.date.end_date)
      .setZone(selectedLocation!.date.time_zone)
      .toFormat("yyyy-MM-dd");
  }, [selectedLocation]);

  const handleAddHotel = async (selectedHotel: HotelInitial) => {
    const tripLodging = await convertHotelIntoTripLodging(
      selectedHotel,
      effectiveCheckIn,
      effectiveCheckOut,
      selectedLocation!.date.time_zone,
      selectedLocation!.date.num_days - 1
    );
    addLodging(tripLodging);
    setLastAddedHotel(tripLodging);
    setShowAddNotification(true);

    setTimeout(() => {
      setShowAddNotification(false);
      setLastAddedHotel(null);
    }, 5000);
  };

  const handleAddHomeStay = async (
    selectedAccommodation: AccommodationListing
  ) => {
    logger.log("selectedAccommodation", selectedAccommodation);
    const tripLodging = convertHomeStayIntoTripLodging(
      selectedAccommodation,
      effectiveCheckIn,
      effectiveCheckOut,
      selectedLocation!.date.time_zone,
      selectedLocation!.date.num_days - 1
    );

    addLodging(tripLodging);
    logger.log("converted tripLodging", tripLodging);
    setLastAddedHotel(tripLodging);
    setShowAddNotification(true);

    // if selectedLocation.accommodation.main is null, set it to the new lodging
    if (!selectedLocation?.accommodation.main) {
      setMainLodging(tripLodging);
    }

    setTimeout(() => {
      setShowAddNotification(false);
      setLastAddedHotel(null);
    }, 5000);
  };

  useEffect(() => {
    let messageInterval: NodeJS.Timeout;

    if (isFetchingHotels || isFetchingHomeStays) {
      messageInterval = setInterval(() => {
        setLoadingMessageIndex((prev) =>
          prev < loadingMessages.length - 1 ? prev + 1 : prev
        );
      }, 1500);
    } else {
      setLoadingMessageIndex(0);
    }

    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [isFetchingHotels, isFetchingHomeStays]);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const memoizedSetViewLodging = useCallback(
    (lodging: ViewLodging) => {
      setViewLodging(lodging);
    },
    [setViewLodging]
  );

  // Memoize markers
  const markers = useMemo(
    () =>
      responseHotels.map((hotel: HotelInitial) => (
        <Marker
          key={hotel.hotelId}
          latitude={hotel.latitude}
          longitude={hotel.longitude}
          style={{
            zIndex:
              viewLodging?.id === hotel.hotelId
                ? 40
                : hoverId === hotel.hotelId
                ? 1
                : 1,
          }}
        >
          <Tooltip
            key={`tooltip-${hotel.hotelId}-${viewLodging?.id}`}
            delay={500}
            closeDelay={0}
            content={
              <div className="flex flex-col items-start justify-start w-64">
                <h3 className="font-medium text-sm py-2">{hotel.hotelName}</h3>
                <div className="w-full h-32 rounded-md overflow-hidden">
                  <LazyLoad
                    src={formatImageUrl(hotel.imageURL, "medium")}
                    lowResSrc={formatImageUrl(hotel.imageURL, "low")}
                    alt={hotel.hotelName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-2 bg-white rounded-b-md w-full space-y-2">
                  <div className="flex items-center justify-start space-x-0.5 w-full">
                    <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md -translate-x-1">
                      <i className="fa-solid fa-star text-[11px]"></i>
                      <span className="text-xs">
                        {formatReviewScore(hotel.reviewScore)}
                      </span>
                    </div>
                    <div className="py-1 text-xs text-neutral-700 rounded-md"></div>
                    <span className="text-xs text-neutral-700 font-light tracking-wide">
                      {formatReviewCount(hotel.reviewCount)} reviews
                    </span>
                  </div>
                  <div className="flex flex-row justify-between w-full items-center">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[15px] font-medium text-black">
                        {formatPrice(hotel.currency, hotel.dailyRate)}
                      </span>
                      <span className="text-neutral-500 text-[13px] font-light">
                        per night
                      </span>
                    </div>
                    {isAdmin && (
                      <button
                        className="flex items-center space-x-2 mt-1 py-0.5 rounded-md bg-indigo-700 hover:bg-indigo-800 font-medium px-2 transition-all duration-500 ease-in-out"
                        onClick={() => handleAddHotel(hotel)}
                      >
                        <i
                          className="fa-solid fa-plus text-white fa-xs text-[11px]"
                          aria-hidden="true"
                        ></i>
                        <p className="text-[11px] text-white">Add</p>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            }
            placement="top"
            showArrow={true}
          >
            <div className="flex flex-col items-center">
              <div
                className={`mb-1 px-2 py-0.5 bg-white rounded-full shadow-sm transition-opacity duration-300 ${
                  viewLodging?.id && viewLodging.id !== hotel.hotelId
                    ? "opacity-50"
                    : ""
                }`}
              >
                <span className="text-xs font-normal">
                  {formatPrice(hotel.currency, hotel.dailyRate)}
                </span>
              </div>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:scale-110 ${
                  viewLodging?.id === hotel.hotelId
                    ? "bg-white text-indigo-600 ring-2 ring-indigo-600 scale-110"
                    : `text-indigo-600 bg-white hover:bg-gray-50 ${
                        viewLodging?.id ? "opacity-50" : ""
                      }`
                }`}
                onMouseEnter={() => setHoverId(hotel.hotelId)}
                onMouseLeave={() => setHoverId(null)}
                onClick={() => handleModalOpenMainLodging(hotel)}
              >
                <div className="relative">
                  <MapPinIcon className="h-4 w-4 drop-shadow-sm transition-transform duration-200" />
                  {hotel.reviewScore > 9 && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  )}
                </div>
              </div>
            </div>
          </Tooltip>
        </Marker>
      )),
    [responseHotels, viewLodging, hoverId]
  );

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative bg-neutral-100 rounded-lg overflow-hidden"
    >
      <div className="absolute top-4 right-4 z-40 flex items-start gap-3">
        <AnimatePresence>
          {showAddNotification && lastAddedHotel && !isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{
                duration: 0.3,
                ease: "easeOut",
              }}
              className="bg-white rounded-lg shadow-lg flex items-start gap-3 pl-3 pr-4 py-2 hover:shadow-xl transition-shadow duration-200"
            >
              <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0">
                <img
                  src={formatImageUrl(lastAddedHotel.mainImageURL, "high")}
                  alt={lastAddedHotel.name}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="flex flex-col justify-between space-y-1">
                <span
                  className="text-sm font-medium text-neutral-800 truncate max-w-[220px]"
                  title={lastAddedHotel.name}
                >
                  {lastAddedHotel.name}
                </span>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="text-xs text-neutral-500 font-light">
                    Added to {locationName}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative">
          <button
            onClick={handleSidebarToggle}
            className="bg-white h-9 w-9 flex items-center justify-center rounded-lg shadow-md hover:bg-gray-50 transition-all duration-300 relative"
            aria-label="Toggle lodgings sidebar"
          >
            <div className="w-7 h-7 flex items-center justify-center group">
              <motion.span
                className="absolute w-5 bg-neutral-500 h-[1px] rounded-full"
                animate={{
                  rotate: isSidebarOpen ? 45 : 0,
                  y: isSidebarOpen ? 0 : -5,
                }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="absolute w-5 bg-neutral-500 h-[1px] rounded-full"
                animate={{
                  opacity: isSidebarOpen ? 0 : 1,
                }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="absolute w-5 bg-neutral-500 h-[1px] rounded-full"
                animate={{
                  rotate: isSidebarOpen ? -45 : 0,
                  y: isSidebarOpen ? 0 : 5,
                }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </button>

          {lodgings.length > 0 && (
            <div
              className={`absolute -top-2 -right-2 w-5 h-5 bg-indigo-700 rounded-full flex items-center justify-center transition-opacity duration-200 ${
                isSidebarOpen ? "opacity-0" : "opacity-100"
              }`}
            >
              <span className="text-[10px] font-medium text-white">
                {isAdmin
                  ? lodgings.length
                  : lodgings.filter(
                      (lodging) =>
                        lodging.id === selectedLocation?.accommodation.main?.id
                    ).length}
              </span>
            </div>
          )}
        </div>
      </div>

      <Map
        mapboxAccessToken={mapboxToken}
        ref={mapRef}
        {...accommodationMapViewport}
        onMove={onMove}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
        attributionControl={false}
        reuseMaps={true}
      >
        {activeAccommodationTab === "hotels" && <>{markers}</>}

        {activeAccommodationTab === "home-stays" &&
          responseHomeStays.map((homeStay: AccommodationListing) => (
            <Marker
              key={homeStay.listingid}
              latitude={Number(homeStay.latitude)}
              longitude={Number(homeStay.longitude)}
              style={{
                zIndex:
                  viewLodging?.id === Number(homeStay.listingid)
                    ? 20
                    : hoverId === Number(homeStay.listingid)
                    ? 1
                    : 1,
              }}
            >
              <Tooltip
                key={`tooltip-${homeStay.listingid}-${viewLodging?.id}`}
                delay={0}
                closeDelay={0}
                content={
                  <div className="flex flex-col items-start justify-start w-64">
                    <h3 className="font-semibold text-sm py-2">
                      {homeStay.listing_name}
                    </h3>
                    <div className="w-full h-32 rounded-md overflow-hidden">
                      <LazyLoad
                        src={formatImageUrl(homeStay.image_01, "high")}
                        lowResSrc={formatImageUrl(homeStay.image_01, "low")}
                        alt={homeStay.listing_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2 bg-white rounded-b-md w-full">
                      <div className="flex flex-row items-center justify-start space-x-1 w-full mb-3 mt-1">
                        <Home className="h-3.5 w-3.5" />
                        <p className="text-xs text-neutral-600 font-light">
                          {homeStay.property_type}
                        </p>
                      </div>
                      <div className="flex flex-row mt-2 space-x-2 font-light">
                        <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md ">
                          <Bed className="h-3 w-3" />
                          <p className="text-xs">{homeStay.bedroom_count}</p>
                        </div>
                        <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md">
                          <Bath className="h-3 w-3" />
                          <p className="text-xs">{homeStay.bathroom_count}</p>
                        </div>
                        <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md">
                          <Users className="h-3 w-3" />
                          <p className="text-xs">{homeStay.guest_count}</p>
                        </div>
                      </div>
                      <div className="flex flex-row justify-between w-full mt-1 items-center">
                        {homeStay.price ? (
                          <p className="text-[15px] text-black mt-2 font-medium">
                            {formatPrice(homeStay.currency, homeStay.price)}{" "}
                            <span className="text-neutral-500 text-[13px] font-light">
                              per night
                            </span>
                          </p>
                        ) : (
                          <p className="text-neutral-500 text-[13px] font-light">
                            Price on request
                          </p>
                        )}
                        <button
                          onClick={() => handleAddHomeStay(homeStay)}
                          className="flex items-center space-x-2 mt-1 py-0.5 rounded-md bg-indigo-700 hover:bg-indigo-800 font-medium px-2 transition-all duration-500 ease-in-out"
                        >
                          <i
                            className="fa-solid fa-plus text-white fa-xs text-[11px]"
                            aria-hidden="true"
                          ></i>
                          <p className="text-[11px] text-white">Add</p>
                        </button>
                      </div>
                    </div>
                  </div>
                }
                placement="top"
                showArrow={true}
              >
                <div className="flex flex-col items-center">
                  {homeStay.price && (
                    <div
                      className={`mb-1 px-2 py-0.5 bg-white rounded-full shadow-sm transition-opacity duration-300 ${
                        viewLodging?.id &&
                        viewLodging.id !== Number(homeStay.listingid)
                          ? "opacity-50"
                          : ""
                      }`}
                    >
                      <span className="text-xs font-normal">
                        {formatPrice(homeStay.currency, homeStay.price)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs cursor-pointer transition-all duration-300 shadow-md hover:shadow-lg hover:scale-110 ${
                      viewLodging?.id === Number(homeStay.listingid)
                        ? "bg-white text-indigo-600 ring-2 ring-indigo-600 scale-110"
                        : `text-indigo-600 bg-white hover:bg-gray-50 ${
                            viewLodging?.id ? "opacity-50" : ""
                          }`
                    }`}
                    onMouseEnter={() => setHoverId(Number(homeStay.listingid))}
                    onMouseLeave={() => setHoverId(null)}
                    onClick={() => handleModalOpenHomeStay(homeStay)}
                  >
                    <div className="relative">
                      <MapPinIcon className="h-4 w-4 drop-shadow-sm" />
                    </div>
                  </div>
                </div>
              </Tooltip>
            </Marker>
          ))}
      </Map>

      <button
        className={`absolute z-40 bottom-4 left-1/2 flex flex-row items-center justify-center transform -translate-x-1/2 bg-white px-4 py-2 rounded-full duration-300 shadow-md text-sm font-medium ${
          isLoading
            ? "text-gray-400 bg-gray-100"
            : "text-gray-700 hover:bg-gray-50"
        }
        ${isSidebarOpen ? "opacity-0" : ""}`}
        onClick={handleSearchThisArea}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            <span>{loadingMessages[loadingMessageIndex]}</span>
          </>
        ) : (
          <>
            <Search className="w-4 h-4 mr-2" />
            <span>Search this area</span>
          </>
        )}
      </button>

      <FloatingHeader
        isOpen={isSidebarOpen}
        mainLodging={selectedLocation?.accommodation.main ?? null}
        tripId={tripData!.id}
        effectiveCheckIn={effectiveCheckIn}
        effectiveCheckOut={effectiveCheckOut}
      />

      <LodgingsSidebar
        isOpen={isSidebarOpen}
        onClose={handleSidebarClose}
        lodgings={lodgings}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        mainLodging={selectedLocation?.accommodation.main ?? null}
        setViewLodging={memoizedSetViewLodging}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default LodgingsEmptyComponent;
